(() => {
  'use strict';

  const VERSION = 6;
  if (window.WUBSizeGuide?.version === VERSION) return;

  const i18n = window.WUB?.i18n?.sizeGuide || {};
  const dialogs = new Map();

  function dialogFor(id) {
    if (!id) return null;
    const cached = dialogs.get(id);
    if (cached?.isConnected && cached.id === id) return cached;

    const current = document.getElementById(id);
    if (current) dialogs.set(id, current);
    else dialogs.delete(id);
    return current || null;
  }

  function tableHeaders(table) {
    const theadHeaders = Array.from(table.querySelectorAll('thead th'));
    if (theadHeaders.length) return theadHeaders.map((cell) => cell.textContent.trim());

    const firstRow = table.querySelector('tr');
    if (!firstRow) return [];
    const headers = Array.from(firstRow.children).filter((cell) => cell.tagName === 'TH');
    return headers.map((cell) => cell.textContent.trim());
  }

  function tableRows(table) {
    const bodyRows = Array.from(table.querySelectorAll('tbody tr'));
    if (bodyRows.length) return bodyRows;

    return Array.from(table.querySelectorAll('tr')).filter((row) => {
      const cells = Array.from(row.children);
      return cells.some((cell) => cell.tagName === 'TD');
    });
  }

  function cloneChildren(source, target) {
    Array.from(source.childNodes).forEach((node) => target.appendChild(node.cloneNode(true)));
  }

  function directChildContaining(root, node) {
    let current = node;
    while (current && current.parentElement !== root) current = current.parentElement;
    return current?.parentElement === root ? current : null;
  }

  function guidanceBlocksBefore(table) {
    const allowedTags = new Set(['P', 'H2', 'H3', 'OL', 'UL']);
    let root = table.closest('.rte') || table.parentElement;

    for (let depth = 0; root && depth < 4; depth += 1) {
      const tableChild = directChildContaining(root, table);
      if (tableChild) {
        const blocks = [];
        for (const child of root.children) {
          if (child === tableChild) break;
          if (allowedTags.has(child.tagName) && child.textContent.trim()) blocks.push(child);
        }
        if (blocks.length) return blocks;
      }

      if (root.tagName === 'MAIN') break;
      root = root.parentElement;
    }

    return [];
  }

  function renderPlainGuidance(source) {
    const blocks = guidanceBlocksBefore(source);
    if (!blocks.length) return null;

    const guidance = document.createElement('div');
    guidance.className = 'wub-size-guide__guidance';
    let introAssigned = false;

    blocks.forEach((block) => {
      const clone = block.cloneNode(true);
      if (!introAssigned && clone.tagName === 'P') {
        clone.classList.add('wub-size-guide__guidance-intro');
        introAssigned = true;
      }
      guidance.appendChild(clone);
    });

    return guidance;
  }

  function renderPlainTable(source, target) {
    const headers = tableHeaders(source);
    const rows = tableRows(source);
    const columnCount = Math.max(headers.length, ...rows.map((row) => row.children.length), 0);
    const fragment = document.createDocumentFragment();
    const guidance = renderPlainGuidance(source);

    if (guidance) fragment.appendChild(guidance);

    const wrap = document.createElement('div');
    wrap.className = 'wub-size-guide__table-wrap';
    wrap.dataset.columns = String(columnCount);

    const table = source.cloneNode(true);
    table.classList.add('wub-size-guide__table');
    wrap.appendChild(table);

    if (rows.length) {
      const cards = document.createElement('div');
      cards.className = 'wub-size-guide__cards';

      rows.forEach((row) => {
        const card = document.createElement('article');
        card.className = 'wub-size-guide__card';

        Array.from(row.children).forEach((cell, index) => {
          const item = document.createElement('div');
          item.className = `wub-size-guide__card-item${index === 0 ? ' wub-size-guide__card-item--primary' : ''}`;

          const label = document.createElement('div');
          label.className = 'wub-size-guide__card-label';
          label.textContent = headers[index] || '';

          const value = document.createElement('div');
          value.className = 'wub-size-guide__card-value';
          cloneChildren(cell, value);

          if (label.textContent) item.appendChild(label);
          item.appendChild(value);
          card.appendChild(item);
        });

        cards.appendChild(card);
      });

      wrap.appendChild(cards);
    }

    fragment.appendChild(wrap);
    target.replaceChildren(fragment);
  }

  function renderStructuredComparison(source, target) {
    const clone = source.cloneNode(true);
    clone.classList.add('wub-size-guide__comparison');
    target.replaceChildren(clone);
  }

  async function load(dialog) {
    if (!dialog || dialog.dataset.loaded === 'true' || dialog.dataset.loading === 'true') return;
    const url = dialog.dataset.sourceUrl;
    const target = dialog.querySelector('[data-wub-size-guide-content]');
    if (!url || !target) return;

    dialog.dataset.loading = 'true';
    try {
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const source = parsed.querySelector('.wub-comparison-table, main table, .rte table');
      if (!source) throw new Error('Size chart table not found');

      if (source.matches('table')) renderPlainTable(source, target);
      else renderStructuredComparison(source, target);

      dialog.dataset.loaded = 'true';
    } catch (error) {
      const fallback = document.createElement('p');
      fallback.className = 'wub-size-guide__fallback';
      const link = document.createElement('a');
      link.href = url;
      link.textContent = i18n.viewTable || 'View the size chart';
      fallback.append(`${i18n.loadFailedPrefix || 'The size chart could not be loaded here.'} `, link);
      target.replaceChildren(fallback);
    } finally {
      dialog.dataset.loading = 'false';
    }
  }

  async function open(id) {
    const dialog = dialogFor(id);
    if (!dialog) return;
    await load(dialog);
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
  }

  function close(dialog) {
    if (!dialog) return;
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
  }

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-wub-size-guide-open]');
    if (opener) {
      event.preventDefault();
      open(opener.getAttribute('data-wub-size-guide-open'));
      return;
    }

    const closer = event.target.closest('[data-wub-size-guide-close]');
    if (closer) {
      event.preventDefault();
      close(closer.closest('[data-wub-size-guide]'));
    }
  });

  document.addEventListener('click', (event) => {
    const dialog = event.target.closest?.('[data-wub-size-guide]');
    if (dialog && event.target === dialog) close(dialog);
  });

  window.WUBSizeGuide = Object.freeze({ version: VERSION, open });
})();
